import { Box, Button, Paper, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { SuggestionEdit as Suggestion } from '../../types';

export type { SuggestionEdit as Suggestion } from '../../types';

type Status = 'pending' | 'accepted' | 'rejected';

export interface ResolvedSuggestion {
  suggestion: Suggestion;
  status: Status;
  start: number;
  end: number;
}

interface SuggestionReviewProps {
  original: string;
  suggestions: Suggestion[];
  onBack?: () => void;
  onSave?: (finalText: string) => Promise<void> | void;
}

export function locateSuggestions(
  original: string,
  suggestions: Suggestion[]
): ResolvedSuggestion[] {
  const located: ResolvedSuggestion[] = [];
  for (const s of suggestions) {
    if (typeof s.start !== 'number' || typeof s.end !== 'number') continue;
    if (s.start < 0 || s.end > original.length || s.start > s.end) continue;
    located.push({ suggestion: s, status: 'pending', start: s.start, end: s.end });
  }
  located.sort((a, b) => a.start - b.start || a.end - b.end);
  const result: ResolvedSuggestion[] = [];
  let prevEnd = -1;
  for (const r of located) {
    if (r.start < prevEnd) continue;
    result.push(r);
    prevEnd = Math.max(prevEnd, r.end);
  }
  return result;
}

export function applyAccepted(original: string, resolved: ResolvedSuggestion[]): string {
  const accepted = resolved
    .filter(r => r.status === 'accepted')
    .slice()
    .sort((a, b) => b.start - a.start || b.end - a.end);
  let text = original;
  for (const r of accepted) {
    const s = r.suggestion;
    if (s.type === 'replace') {
      text = text.slice(0, r.start) + s.replacement + text.slice(r.end);
    } else if (s.type === 'delete') {
      text = text.slice(0, r.start) + text.slice(r.end);
    } else {
      text = text.slice(0, r.start) + s.text + text.slice(r.start);
    }
  }
  return text;
}

interface Segment {
  text: string;
  resolvedIndex: number;
  kind: 'plain' | 'del' | 'ins';
}

function buildSegments(original: string, resolved: ResolvedSuggestion[]): Segment[] {
  const segs: Segment[] = [];
  let cursor = 0;
  const ordered = resolved.map((r, i) => ({ r, i })).sort((a, b) => a.r.start - b.r.start);

  for (const { r, i } of ordered) {
    if (r.start > cursor) {
      segs.push({ text: original.slice(cursor, r.start), resolvedIndex: -1, kind: 'plain' });
    }
    const s = r.suggestion;
    if (r.status === 'accepted') {
      if (s.type === 'replace') {
        segs.push({ text: s.replacement, resolvedIndex: -1, kind: 'plain' });
      } else if (s.type === 'insert') {
        segs.push({ text: s.text, resolvedIndex: -1, kind: 'plain' });
      }
    } else if (r.status === 'rejected') {
      if (s.type === 'replace' || s.type === 'delete') {
        segs.push({ text: original.slice(r.start, r.end), resolvedIndex: -1, kind: 'plain' });
      }
    } else {
      if (s.type === 'replace') {
        segs.push({ text: s.replacement, resolvedIndex: i, kind: 'ins' });
        segs.push({ text: original.slice(r.start, r.end), resolvedIndex: i, kind: 'del' });
      } else if (s.type === 'delete') {
        segs.push({ text: original.slice(r.start, r.end), resolvedIndex: i, kind: 'del' });
      } else {
        segs.push({ text: s.text, resolvedIndex: i, kind: 'ins' });
      }
    }
    cursor = Math.max(cursor, r.end);
  }
  if (cursor < original.length) {
    segs.push({ text: original.slice(cursor), resolvedIndex: -1, kind: 'plain' });
  }
  return segs;
}

function describeSuggestion(s: Suggestion): { label: string; preview: React.ReactNode } {
  if (s.type === 'replace') {
    return {
      label: 'Replace',
      preview: (
        <>
          <Box component="span" sx={{ color: 'success.main' }}>
            {s.replacement}
          </Box>
          <Box component="span" sx={{ mx: 0.5 }}>
            ←
          </Box>
          <Box component="span" sx={{ textDecoration: 'line-through', color: 'error.main' }}>
            {s.find}
          </Box>
        </>
      ),
    };
  }
  if (s.type === 'delete') {
    return {
      label: 'Delete',
      preview: (
        <Box component="span" sx={{ textDecoration: 'line-through', color: 'error.main' }}>
          {s.find}
        </Box>
      ),
    };
  }
  return {
    label: 'Insert',
    preview: (
      <Box component="span" sx={{ color: 'success.main' }}>
        {s.text}
      </Box>
    ),
  };
}

const SuggestionReview: React.FC<SuggestionReviewProps> = ({
  original,
  suggestions,
  onBack,
  onSave,
}) => {
  const [resolved, setResolved] = useState<ResolvedSuggestion[]>(() =>
    locateSuggestions(original, suggestions)
  );
  const [editedText, setEditedText] = useState('');
  const [editing, setEditing] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ x: number; y: number; key: number } | null>(null);

  const spanRefs = useRef<Record<number, HTMLSpanElement | null>>({});
  const cardRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const textPaneRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const cardPaneRef = useRef<HTMLDivElement | null>(null);
  const scrollTargetRef = useRef<number | null>(null);
  const pendingScrollTopRef = useRef<number | null>(null);
  const toastTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (saving) return;
    const located = locateSuggestions(original, suggestions);
    spanRefs.current = {};
    cardRefs.current = {};
    setResolved(located);
    setFocusedIndex(-1);
    setEditing(false);
    setEditedText(original);
    scrollTargetRef.current = located.findIndex(r => r.status === 'pending');
  }, [original, suggestions, saving]);

  const segments = useMemo(() => buildSegments(original, resolved), [original, resolved]);

  const pendingCount = resolved.filter(r => r.status === 'pending').length;
  const resolvedCount = resolved.length - pendingCount;
  const allResolved = resolved.length > 0 && pendingCount === 0;

  useEffect(() => {
    const target = scrollTargetRef.current;
    if (target == null || target < 0) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        const span = spanRefs.current[target];
        const textPane = textPaneRef.current;
        if (span && textPane) {
          const spanRect = span.getBoundingClientRect();
          const paneRect = textPane.getBoundingClientRect();
          const delta = spanRect.top - paneRect.top;
          textPane.scrollTop = Math.max(0, textPane.scrollTop + delta - 8);
        }
        const card = cardRefs.current[target];
        const cardPane = cardPaneRef.current;
        if (card && cardPane) {
          const cardRect = card.getBoundingClientRect();
          const paneRect = cardPane.getBoundingClientRect();
          const delta = cardRect.top - paneRect.top;
          cardPane.scrollTop = Math.max(0, cardPane.scrollTop + delta - 8);
        }
        scrollTargetRef.current = null;
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [resolved]);

  useEffect(
    () => () => {
      if (toastTimerRef.current != null) window.clearTimeout(toastTimerRef.current);
    },
    []
  );

  const setStatus = (index: number, status: Status) => {
    setResolved(prev => {
      const next = prev.map((r, i) => (i === index ? { ...r, status } : r));
      if (status !== 'pending') {
        const nextPending = next.findIndex((r, i) => r.status === 'pending' && i > index);
        const fallbackPending = next.findIndex(r => r.status === 'pending');
        scrollTargetRef.current = nextPending >= 0 ? nextPending : fallbackPending;
      }
      return next;
    });
    if (focusedIndex === index) setFocusedIndex(-1);
  };

  const setAll = (status: Status) => {
    setResolved(prev => prev.map(r => ({ ...r, status })));
    scrollTargetRef.current = -1;
  };

  const restore = () => {
    const located = locateSuggestions(original, suggestions);
    setResolved(located);
    setFocusedIndex(-1);
    scrollTargetRef.current = located.findIndex(r => r.status === 'pending');
  };

  const scrollChildIntoView = (
    pane: HTMLElement | null,
    child: HTMLElement | null,
    alignTop: boolean
  ) => {
    if (!pane || !child) return;
    const paneRect = pane.getBoundingClientRect();
    const childRect = child.getBoundingClientRect();
    const delta = childRect.top - paneRect.top;
    const offset = alignTop ? 8 : (paneRect.height - childRect.height) / 2;
    pane.scrollTop = Math.max(0, pane.scrollTop + delta - offset);
  };

  const handleFocus = (i: number) => {
    setFocusedIndex(i);
    scrollChildIntoView(cardPaneRef.current, cardRefs.current[i], true);
    scrollChildIntoView(textPaneRef.current, spanRefs.current[i], false);
  };

  const showLockedToast = (e: React.MouseEvent<HTMLDivElement>) => {
    const pane = textPaneRef.current;
    if (!pane) return;
    const rect = pane.getBoundingClientRect();
    setToast({
      x: e.clientX - rect.left + pane.scrollLeft,
      y: e.clientY - rect.top + pane.scrollTop,
      key: Date.now(),
    });
    if (toastTimerRef.current != null) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 1800);
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    await onSave(editedText);
    setSaving(false);
  };

  useEffect(() => {
    if (allResolved && !editing) {
      pendingScrollTopRef.current = textPaneRef.current?.scrollTop ?? 0;
      setEditedText(applyAccepted(original, resolved));
      setEditing(true);
    }
    if (!allResolved && editing) {
      pendingScrollTopRef.current = textareaRef.current?.scrollTop ?? 0;
      setEditing(false);
    }
  }, [allResolved, editing, original, resolved]);

  useEffect(() => {
    const target = pendingScrollTopRef.current;
    if (target == null) return;
    const raf = requestAnimationFrame(() => {
      if (editing) {
        const ta = textareaRef.current;
        if (ta) ta.scrollTop = target;
      } else {
        const pane = textPaneRef.current;
        if (pane) pane.scrollTop = target;
      }
      pendingScrollTopRef.current = null;
    });
    return () => cancelAnimationFrame(raf);
  }, [editing]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 200, gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {resolvedCount} of {resolved.length} resolved
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {onBack && (
            <Button size="small" variant="text" onClick={onBack} disabled={saving}>
              Back
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={() => setAll('accepted')}
            disabled={saving}
          >
            Accept all
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setAll('rejected')}
            disabled={saving}
          >
            Reject all
          </Button>
          <Button size="small" variant="outlined" onClick={restore} disabled={saving}>
            Restore all
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!allResolved || saving || !onSave}
            onClick={handleSave}
          >
            {saving ? 'Saving…' : 'Confirm changes'}
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 1.5 }}>
        <Box
          ref={textPaneRef}
          onClick={editing ? undefined : showLockedToast}
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: editing ? 'hidden' : 'scroll',
            overflowX: 'hidden',
            scrollbarGutter: 'stable',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: 1,
            bgcolor: 'background.paper',
            p: editing ? 0 : 1.5,
            fontSize: '0.95rem',
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
            position: 'relative',
          }}
        >
          {editing ? (
            <Box
              component="textarea"
              ref={textareaRef}
              value={editedText}
              onChange={e => setEditedText(e.target.value)}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                border: 'none',
                outline: 'none',
                resize: 'none',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                background: 'transparent',
                p: 1.5,
                whiteSpace: 'pre-wrap',
                overflowY: 'auto',
                scrollbarGutter: 'stable',
                boxSizing: 'border-box',
              }}
            />
          ) : (
            segments.map((seg, idx) => {
              if (seg.kind === 'plain') {
                return <span key={idx}>{seg.text}</span>;
              }
              const isFocused = focusedIndex === seg.resolvedIndex;
              const isDel = seg.kind === 'del';
              return (
                <Box
                  key={idx}
                  component="span"
                  ref={(el: HTMLSpanElement | null) => {
                    if (el) spanRefs.current[seg.resolvedIndex] = el;
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    handleFocus(seg.resolvedIndex);
                  }}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: isDel
                      ? isFocused
                        ? 'rgba(244, 67, 54, 0.35)'
                        : 'rgba(244, 67, 54, 0.18)'
                      : isFocused
                        ? 'rgba(76, 175, 80, 0.35)'
                        : 'rgba(76, 175, 80, 0.18)',
                    textDecoration: isDel ? 'line-through' : 'none',
                    borderRadius: 0.5,
                    px: 0.25,
                    outline: isFocused ? '2px solid rgba(25, 118, 210, 0.6)' : 'none',
                  }}
                >
                  {seg.text}
                </Box>
              );
            })
          )}
          {toast && (
            <Box
              key={toast.key}
              sx={{
                position: 'absolute',
                left: toast.x,
                top: toast.y + 14,
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                fontSize: '0.75rem',
                px: 1,
                py: 0.5,
                borderRadius: 0.5,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
                zIndex: 1,
                animation: 'suggestionToastFade 1.8s ease-out forwards',
                '@keyframes suggestionToastFade': {
                  '0%': { opacity: 0, transform: 'translate(-50%, -4px)' },
                  '10%': { opacity: 1, transform: 'translate(-50%, 0)' },
                  '80%': { opacity: 1, transform: 'translate(-50%, 0)' },
                  '100%': { opacity: 0, transform: 'translate(-50%, -4px)' },
                },
              }}
            >
              Resolve all suggestions before editing
            </Box>
          )}
        </Box>
        <Box
          ref={cardPaneRef}
          sx={{
            width: 280,
            flexShrink: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            position: 'relative',
          }}
        >
          {editing && (
            <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
              All suggestions resolved. Edit the policy directly, then confirm.
            </Typography>
          )}
          {!editing &&
            resolved.map((r, i) => {
              if (r.status !== 'pending') return null;
              const { label, preview } = describeSuggestion(r.suggestion);
              const isFocused = focusedIndex === i;
              return (
                <Paper
                  key={r.suggestion.id}
                  ref={(el: HTMLDivElement | null) => {
                    cardRefs.current[i] = el;
                  }}
                  variant="outlined"
                  onClick={() => handleFocus(i)}
                  sx={{
                    p: 1.25,
                    cursor: 'pointer',
                    borderColor: isFocused ? 'primary.main' : undefined,
                    borderWidth: isFocused ? 2 : 1,
                  }}
                >
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {label}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontSize: '0.85rem', wordBreak: 'break-word' }}
                  >
                    {preview}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={e => {
                        e.stopPropagation();
                        setStatus(i, 'accepted');
                      }}
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      Accept
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={e => {
                        e.stopPropagation();
                        setStatus(i, 'rejected');
                      }}
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      Reject
                    </Button>
                  </Box>
                </Paper>
              );
            })}
        </Box>
      </Box>
    </Box>
  );
};

export default SuggestionReview;
